import { useEffect, useRef } from 'react';
import { Box, ThemeProvider } from '@mui/material';
import { TestIds } from '../../../assets/testIds';
import { ContentTheme } from './ContentTheme';

interface ContentArticleBodyViewProps {
  richText?: string | null;
}

// Marks the <figure class="table"> around each article-table as its scroll wrapper. Done here
// rather than with figure:has(> table) so the scroll also works in browsers without :has().
const TABLE_SCROLLER_ATTRIBUTE = 'data-article-table-scroller';

// Safari doesn't make scroll containers focusable, so keyboard users couldn't reach the columns
// of a table that overflows its figure. Make each table's scroll wrapper a named, focusable region.
function setUpTableScrollers(body: HTMLElement) {
  body.querySelectorAll<HTMLElement>('figure.table').forEach((figure) => {
    const table = figure.querySelector(':scope > table.article-table');
    if (!table) return;
    const caption = figure.querySelector('figcaption') ?? table.querySelector('caption');
    figure.setAttribute(TABLE_SCROLLER_ATTRIBUTE, '');
    figure.setAttribute('tabindex', '0');
    figure.setAttribute('role', 'region');
    if (!figure.hasAttribute('aria-label')) {
      figure.setAttribute('aria-label', caption?.textContent?.trim() || 'Data table');
    }
  });
}

function ArticleRichText({ richText }: {
  richText: string;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);

  // This edits DOM that React owns through dangerouslySetInnerHTML. React only rewrites that DOM
  // when richText changes, so the effect must stay keyed on richText to re-run after each rewrite.
  useEffect(() => {
    if (bodyRef.current) setUpTableScrollers(bodyRef.current);
  }, [richText]);

  return (
    <Box sx={(theme) => {
      const paragraphSx = {
        fontSize: theme.spacing(1.75),
        lineHeight: 1.43,
        letterSpacing: '0.17px',
        fontWeight: 400,
      };
      const captionSx = {
        fontSize: theme.spacing(1.5),
        lineHeight: 1.66,
        letterSpacing: '0.4px',
        color: `${theme.palette.text.secondary} !important`,
      };
      return {
        '& p, & span, & li, & td, & h1, & h2, & h3, & h4, & h5, & h6': {
          color: `${theme.palette.text.primary} !important`,
        },
        '& > div': {
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(2.5),
        },
        '& > div > *': { margin: 0 },
        '& p, & li, & td': paragraphSx,
        '& h2': {
          fontSize: theme.spacing(3),
          lineHeight: 1.334,
          letterSpacing: 0,
          fontWeight: 400,
        },
        '& h3': {
          fontSize: theme.spacing(2.5),
          lineHeight: 1.6,
          letterSpacing: '0.15px',
          fontWeight: 500,
        },
        '& ul, & ol': {
          pl: theme.spacing(3),
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(0.75),
        },
        '& li > p': { margin: 0 },
        '& figure': { margin: 0 },
        '& figcaption, & figcaption *, & .caption': captionSx,
        '& img': { maxWidth: '100%', height: 'auto', borderRadius: theme.spacing(0.75) },
        '& a': { color: theme.palette.brand.primary },
        '& hr': {
          width: '100%',
          border: 0,
          borderTop: `1px solid ${theme.palette.divider}`,
        },
        '& > div > h2:not(:first-child)': {
          borderTop: `1px solid ${theme.palette.divider}`,
          pt: theme.spacing(2.5),
        },
        '& > div > hr + h2:not(:first-child)': { borderTop: 0, pt: 0 },
        // Editors opt a table into the data table look with class="article-table". CKEditor wraps
        // every table in <figure class="table">, which scrolls sideways when the columns don't fit.
        [`& figure.table[${TABLE_SCROLLER_ATTRIBUTE}]`]: { overflowX: 'auto' },
        '& table.article-table': {
          width: '100%',
          borderCollapse: 'separate',
          borderSpacing: 0,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.spacing(0.75),
          backgroundColor: theme.palette.background.paper,
          textAlign: 'left',
        },
        '& table.article-table :is(th, td)': {
          p: theme.spacing(1.5),
          borderBottom: `1px solid ${theme.palette.divider}`,
          fontSize: '13px',
          lineHeight: 'normal',
          letterSpacing: 'normal',
          fontWeight: 400,
          textAlign: 'left',
          verticalAlign: 'top',
        },
        '& table.article-table thead th': {
          fontSize: '11px',
          fontWeight: 600,
          color: `${theme.palette.text.secondary} !important`,
        },
        '& table.article-table tbody :is(th, td):first-child': { fontWeight: 500 },
        '& table.article-table tbody tr:last-child > :is(th, td)': { borderBottom: 0 },
        // Pasted cells wrap their text in <p>/<span>; let the cell set the type.
        '& table.article-table :is(th, td) :is(p, span)': {
          margin: 0,
          font: 'inherit',
          letterSpacing: 'inherit',
          color: 'inherit !important',
        },
      };
    }}>
      <div
        ref={bodyRef}
        data-testid={TestIds.contentArticleView.contentBody}
        dangerouslySetInnerHTML={{ __html: richText }}
      />
    </Box>
  );
}

// Article body type scale: H2, H3, paragraph, list, caption. Lists share the paragraph size so
// they never outrank body copy. Blocks follow a 20px rhythm and every H2 after the first opens a
// new section with a divider, so a long scroll has visual structure. Tables classed
// `article-table` render as a bordered data table with a muted header row.
export default function ContentArticleBodyView(props: ContentArticleBodyViewProps) {
  if (!props.richText) return null;

  return (
    <ThemeProvider theme={ContentTheme}>
      <ArticleRichText richText={props.richText} />
    </ThemeProvider>
  );
}
